import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';

/** The location and week the scheduling assistant works on — what the builder is showing. */
export interface ScheduleAssistantContext {
  customerCode: number;
  /** First day of the shown week, yyyy-MM-dd (null on screens without a week, e.g. setup). */
  weekStart: string | null;
}

/** A navigation the assistant asked for (AIProxyAPI show_in_ui). Changes no data. */
export interface ScheduleUiCommand {
  kind: string;
  weekStart?: string;
  employeeId?: number;
  shiftTemplateId?: number;
  /** open_today: the day to show, yyyy-MM-dd (empty = today). */
  date?: string;
}

/** Commands the schedule builder carries out itself. */
const BUILDER_KINDS = new Set(['open_week', 'open_coverage', 'open_requests', 'open_employee', 'open_today']);

/** Commands that open a TLM Setup tab: kind → tab key (TlmSetupComponent). */
const SETUP_TABS: { [kind: string]: string } = {
  open_shift_settings: 'ohAndShifts',
  open_scheduling_rules: 'scheduling',
  open_jobs: 'jobs',
};

/**
 * Bridge between the TLM scheduling screens and the AI chat widget (PDP, US-8.113).
 * Screens publish which location (and week) is on screen; the widget is offered only while
 * one is. The widget reports confirmed changes (the builder reloads) and hands over UI commands
 * — the builder runs its own, anything else navigates, and a builder command issued from another
 * screen waits here until the builder is up.
 */
@Injectable({ providedIn: 'root' })
export class ScheduleAssistantContextService {
  private readonly contextSubject = new BehaviorSubject<ScheduleAssistantContext | null>(null);
  readonly context$ = this.contextSubject.asObservable();

  /** Builder toolbar button → open the widget on the scheduling topic. */
  readonly openRequested$ = new Subject<void>();

  /** A change confirmed in the chat was executed. */
  readonly scheduleChanged$ = new Subject<void>();

  /** Commands for the builder that is on screen right now. */
  readonly uiCommand$ = new Subject<ScheduleUiCommand>();

  private builderOnScreen = false;
  private pendingBuilderCommands: ScheduleUiCommand[] = [];

  constructor(private router: Router) {}

  get current(): ScheduleAssistantContext | null {
    return this.contextSubject.value;
  }

  /** A screen shows this location; weekStart omitted = keep the week already known for it. */
  set(customerCode: number, weekStart?: string) {
    if (!customerCode) return;
    const cur = this.contextSubject.value;
    const sameCustomer = cur && cur.customerCode === +customerCode;
    const week = weekStart ?? (sameCustomer ? cur.weekStart : null);
    if (sameCustomer && cur.weekStart === week) return;
    this.contextSubject.next({ customerCode: +customerCode, weekStart: week });
  }

  /** Called when a screen goes away; ignores a stale clear from a location already replaced. */
  clear(customerCode: number) {
    const cur = this.contextSubject.value;
    if (cur && cur.customerCode === +customerCode) this.contextSubject.next(null);
  }

  requestOpen() {
    this.openRequested$.next();
  }

  notifyScheduleChanged() {
    this.scheduleChanged$.next();
  }

  /** The builder registers itself so commands reach it directly instead of via navigation. */
  setBuilderOnScreen(onScreen: boolean) {
    this.builderOnScreen = onScreen;
  }

  /** Builder commands issued before it was on screen, oldest first. */
  takePendingBuilderCommands(): ScheduleUiCommand[] {
    const pending = this.pendingBuilderCommands;
    this.pendingBuilderCommands = [];
    return pending;
  }

  /**
   * Opens the builder of one location and runs a builder command there (FENIKS US-10.5 — a click
   * on a TLM notification). Runs directly when that location's builder is on screen; otherwise it
   * waits here and the builder takes it once it has loaded the location.
   */
  openBuilderAt(customerCode: number, cmd: ScheduleUiCommand) {
    if (!customerCode || !BUILDER_KINDS.has(cmd.kind)) return;
    if (this.builderOnScreen && this.current?.customerCode === +customerCode) {
      this.uiCommand$.next(cmd);
      return;
    }
    this.pendingBuilderCommands.push(cmd);
    this.router.navigate(['/controlpanel/tlm-scheduling'], { queryParams: { customerCode: +customerCode } });
  }

  runUiCommands(commands: ScheduleUiCommand[]) {
    const ctx = this.current;
    if (!ctx || !commands?.length) return;

    for (const cmd of commands) {
      if (BUILDER_KINDS.has(cmd.kind)) {
        if (this.builderOnScreen) {
          this.uiCommand$.next(cmd);
        } else {
          this.pendingBuilderCommands.push(cmd);
          this.router.navigate(['/controlpanel/tlm-scheduling'], { queryParams: { customerCode: ctx.customerCode } });
        }
      } else if (SETUP_TABS[cmd.kind]) {
        this.router.navigate(['/controlpanel/tlm-setup'], {
          queryParams: { customerCode: ctx.customerCode, tab: SETUP_TABS[cmd.kind] },
        });
      }
    }
  }
}
